/**
 * FAQ admin page.
 *
 * Route: /projects/:projectId/faq
 *
 * Lets the admin manage the storefront FAQ that renders at /faq on their
 * public website. Supports:
 *   - list + reorder (arrows)
 *   - create / edit / delete entries
 *   - toggle visibility without deleting
 *   - seed from curated starter templates (general, shipping, returns,
 *     account, e-commerce) — idempotent, never overwrites edits
 *
 * Per-project isolation: every call goes through /projects/:projectId/*
 * endpoints. The admin frontend never reaches into another project's data.
 */
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowUp,
  Eye,
  EyeOff,
  HelpCircle,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiHelpers, type FaqEntry, type FaqTemplateSet } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm-provider';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface EntryDraft {
  question: string;
  body: string;
  category: string;
  isActive: boolean;
}

const EMPTY_DRAFT: EntryDraft = {
  question: '',
  body: '',
  category: '',
  isActive: true,
};

export default function FaqPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState<FaqEntry | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [draft, setDraft] = useState<EntryDraft>(EMPTY_DRAFT);
  const [seedOpen, setSeedOpen] = useState(false);
  const [seedSet, setSeedSet] = useState<string>('ecommerce');
  const [includeDerived, setIncludeDerived] = useState(true);

  const listQuery = useQuery({
    queryKey: ['faq', projectId],
    queryFn: async () => {
      const res = await apiHelpers.listFaqEntries(projectId!);
      return (res.data.data || []) as FaqEntry[];
    },
    enabled: Boolean(projectId),
  });

  const templatesQuery = useQuery({
    queryKey: ['faq-templates', projectId],
    queryFn: async () => {
      const res = await apiHelpers.listFaqTemplates(projectId!);
      return (res.data.data || []) as FaqTemplateSet[];
    },
    enabled: Boolean(projectId) && seedOpen,
  });

  const entries = listQuery.data || [];

  const grouped = useMemo(() => {
    const by: Record<string, FaqEntry[]> = {};
    for (const e of entries) {
      const cat = e.category?.trim() || 'General';
      (by[cat] ||= []).push(e);
    }
    return by;
  }, [entries]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['faq', projectId] });

  const createMutation = useMutation({
    mutationFn: (data: EntryDraft) =>
      apiHelpers.createFaqEntry(projectId!, {
        question: data.question,
        body: data.body,
        category: data.category || undefined,
        isActive: data.isActive,
      }),
    onSuccess: () => {
      toast.success('FAQ entry added');
      invalidate();
      setIsCreating(false);
      setDraft(EMPTY_DRAFT);
    },
    onError: () => toast.error('Failed to create entry'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<EntryDraft> }) =>
      apiHelpers.updateFaqEntry(projectId!, id, {
        question: data.question,
        body: data.body,
        category: data.category,
        isActive: data.isActive,
      }),
    onSuccess: () => {
      toast.success('FAQ entry updated');
      invalidate();
      setEditing(null);
    },
    onError: () => toast.error('Failed to update entry'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiHelpers.deleteFaqEntry(projectId!, id),
    onSuccess: () => {
      toast.success('Entry deleted');
      invalidate();
    },
    onError: () => toast.error('Failed to delete entry'),
  });

  const reorderMutation = useMutation({
    mutationFn: (ids: string[]) => apiHelpers.reorderFaqEntries(projectId!, ids),
    onSuccess: invalidate,
    onError: () => toast.error('Failed to reorder'),
  });

  const seedMutation = useMutation({
    mutationFn: () =>
      apiHelpers.seedFaqFromTemplate(projectId!, {
        set: seedSet,
        includeDerived,
        merge: true,
      }),
    onSuccess: (res) => {
      const inserted = res.data.data?.inserted ?? 0;
      const skipped = res.data.data?.skipped ?? 0;
      if (inserted === 0) {
        toast.info(`No new entries added — ${skipped} already present.`);
      } else {
        toast.success(
          `Seeded ${inserted} entr${inserted === 1 ? 'y' : 'ies'}${
            skipped ? ` (skipped ${skipped} already present)` : ''
          }`
        );
      }
      invalidate();
      setSeedOpen(false);
    },
    onError: () => toast.error('Failed to seed templates'),
  });

  const openEdit = (entry: FaqEntry) => {
    setEditing(entry);
    setDraft({
      question: entry.question,
      body: entry.body,
      category: entry.category || '',
      isActive: entry.isActive,
    });
  };

  const move = (index: number, dir: -1 | 1) => {
    const next = [...entries];
    const swap = index + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[index], next[swap]] = [next[swap], next[index]];
    reorderMutation.mutate(next.map((e) => e._id));
  };

  const handleSubmit = () => {
    if (!draft.question.trim() || !draft.body.trim()) {
      toast.error('Question and answer are required');
      return;
    }
    if (editing) {
      updateMutation.mutate({ id: editing._id, data: draft });
    } else {
      createMutation.mutate(draft);
    }
  };

  const draftOpen = isCreating || Boolean(editing);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">FAQ</h1>
          <p className="text-muted-foreground">
            Manage the Frequently Asked Questions section on your storefront.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setSeedOpen(true)}>
            <Sparkles className="mr-2 h-4 w-4" />
            Seed from templates
          </Button>
          <Button
            onClick={() => {
              setIsCreating(true);
              setDraft(EMPTY_DRAFT);
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            New entry
          </Button>
        </div>
      </div>

      {listQuery.isLoading ? (
        <div className="flex justify-center py-12">
          <div className="border-primary h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
        </div>
      ) : entries.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <HelpCircle className="text-muted-foreground h-12 w-12" />
            <h3 className="mt-4 text-lg font-semibold">No FAQ entries yet</h3>
            <p className="text-muted-foreground max-w-md">
              Start from a curated template — we'll add sensible defaults for shipping, returns,
              payment, and account questions that you can edit to match your store.
            </p>
            <div className="mt-6 flex gap-2">
              <Button onClick={() => setSeedOpen(true)}>
                <Sparkles className="mr-2 h-4 w-4" />
                Seed from templates
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setIsCreating(true);
                  setDraft(EMPTY_DRAFT);
                }}
              >
                <Plus className="mr-2 h-4 w-4" />
                Write your own
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        Object.entries(grouped).map(([category, groupEntries]) => (
          <Card key={category}>
            <CardHeader>
              <CardTitle className="text-lg">{category}</CardTitle>
              <CardDescription>
                {groupEntries.length} {groupEntries.length === 1 ? 'entry' : 'entries'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {groupEntries.map((entry) => {
                const globalIndex = entries.findIndex((e) => e._id === entry._id);
                return (
                  <div key={entry._id} className="flex items-start gap-3 rounded-md border p-3">
                    <div className="flex flex-col gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        disabled={globalIndex === 0 || reorderMutation.isPending}
                        onClick={() => move(globalIndex, -1)}
                      >
                        <ArrowUp className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        disabled={globalIndex === entries.length - 1 || reorderMutation.isPending}
                        onClick={() => move(globalIndex, 1)}
                      >
                        <ArrowDown className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{entry.question}</span>
                        {!entry.isActive && (
                          <Badge variant="outline" className="text-muted-foreground">
                            Hidden
                          </Badge>
                        )}
                        {entry.templateKey && (
                          <Badge variant="secondary" className="text-xs">
                            Template
                          </Badge>
                        )}
                      </div>
                      <p className="text-muted-foreground mt-1 whitespace-pre-wrap text-sm">
                        {entry.body}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() =>
                          updateMutation.mutate({
                            id: entry._id,
                            data: { isActive: !entry.isActive },
                          })
                        }
                        title={entry.isActive ? 'Hide' : 'Show'}
                      >
                        {entry.isActive ? (
                          <Eye className="h-4 w-4" />
                        ) : (
                          <EyeOff className="h-4 w-4" />
                        )}
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(entry)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive"
                        onClick={async () => {
                          if (
                            await confirm({
                              title: 'Delete FAQ?',
                              message: `Delete "${entry.question}"?`,
                              danger: true,
                            })
                          ) {
                            deleteMutation.mutate(entry._id);
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        ))
      )}

      {/* Create / edit dialog */}
      <Dialog
        open={draftOpen}
        onOpenChange={(open) => {
          if (!open) {
            setEditing(null);
            setIsCreating(false);
            setDraft(EMPTY_DRAFT);
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit FAQ entry' : 'New FAQ entry'}</DialogTitle>
            <DialogDescription>
              Shown on your storefront's /faq page. Keep answers concise and plain.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="faq-q">Question</Label>
              <Input
                id="faq-q"
                value={draft.question}
                onChange={(e) => setDraft({ ...draft, question: e.target.value })}
                placeholder="e.g. How long does shipping take?"
                maxLength={500}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="faq-a">Answer</Label>
              <Textarea
                id="faq-a"
                value={draft.body}
                onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                rows={6}
                maxLength={10000}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="faq-cat">Category (optional)</Label>
                <Input
                  id="faq-cat"
                  value={draft.category}
                  onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                  placeholder="Shipping, Returns, …"
                  maxLength={120}
                />
              </div>
              <div className="flex items-end gap-3">
                <Switch
                  id="faq-active"
                  checked={draft.isActive}
                  onCheckedChange={(checked) => setDraft({ ...draft, isActive: checked })}
                />
                <Label htmlFor="faq-active">Visible on storefront</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEditing(null);
                setIsCreating(false);
                setDraft(EMPTY_DRAFT);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {editing ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Seed templates dialog */}
      <Dialog open={seedOpen} onOpenChange={setSeedOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Seed FAQ from template</DialogTitle>
            <DialogDescription>
              Adds curated starter entries to your FAQ. Safe to run more than once — entries you
              already have are never overwritten, and duplicates are skipped automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Template set</Label>
              <Select value={seedSet} onValueChange={setSeedSet}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(templatesQuery.data || []).map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.label} ({t.entryCount})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {templatesQuery.data?.find((t) => t.id === seedSet)?.description && (
                <p className="text-muted-foreground text-sm">
                  {templatesQuery.data.find((t) => t.id === seedSet)?.description}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Switch
                id="include-derived"
                checked={includeDerived}
                onCheckedChange={setIncludeDerived}
              />
              <div>
                <Label htmlFor="include-derived">Include project-specific entries</Label>
                <p className="text-muted-foreground text-xs">
                  Adds extras based on your store config (support email, shipping countries,
                  published legal pages).
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSeedOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => seedMutation.mutate()} disabled={seedMutation.isPending}>
              {seedMutation.isPending ? 'Seeding…' : 'Seed templates'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
